import { z } from 'zod';
import { phoneSchema, storedPhoneSchema } from '../phone/phone.js';
import { paginationQuerySchema } from '../common/pagination.js';
import { inventoryModesSchema, postingStatusSchema } from './inventory.contract.js';
import { actorSchema, containerTypeSchema, fractionSchema, branchRefSchema, businessDateSchema, customerLinkErrorSchema, customerLinkStatusSchema, nonNegativeMoneySchema, phoneVerificationIdSchema, positiveMoneySchema, productSchema, transactionListQuerySchema, } from './primitives.js';
/**
 * The Fuel PDA contracts (docs/rebuild/fuel-pda.md sections 5, 6 and 7): the
 * PDA application's endpoints under `/pda`, and the dashboard additions for
 * purchase customers and nationalities. Money never appears on a PDA endpoint.
 */
// --- shared ---------------------------------------------------------------
/**
 * A phone as typed or scanned: the shared phone rule (customers.md section 9)
 * normalises it to E.164 and accepts a mobile only; a refusal is
 * `PHONE_REQUIRED`, `PHONE_INVALID` or `PHONE_NOT_MOBILE` on `phone`.
 */
export const pdaPhoneSchema = phoneSchema;
/** A transaction reference as printed or scanned, e.g. `SA-2026-12363`; trimmed and upper-cased. */
export const pdaReferenceSchema = z
    .string()
    .trim()
    .min(1)
    .max(40)
    .transform((value) => value.toUpperCase());
/** One counted container quantity: a whole number >= 0. */
const containerCountSchema = z.number().int().min(0).max(100_000);
/**
 * عدد الجراكل المخصومة (D): a decimal >= 0 with up to five decimal places
 * (fuel-pda.md section 3), sent as a decimal string. A JSON number is still
 * accepted so a PDA released before the decimal deduction keeps working.
 */
const deductedJerrycansSchema = z
    .union([z.string(), z.number()])
    .transform(String)
    .pipe(nonNegativeMoneySchema)
    .refine((value) => Number(value) <= 100_000, 'must not exceed 100000');
/**
 * A quantity option the administrator configured (a Fuel category, fuel.md
 * section 5.9): the PDA lists these instead of any fixed container sizes.
 */
export const pdaQuantityOptionSchema = z.object({
    id: z.string(),
    type: containerTypeSchema,
    /** In the request's language (Arabic fallback). */
    name: z.string(),
    nameAr: z.string(),
    nameEn: z.string().nullable(),
    /** The stored fraction of one container, for example `0.33333`. */
    fraction: fractionSchema,
});
/**
 * B or J as a decimal string. Accepts a number too, so a client opened before
 * the KAN-32 deploy still reads a receipt (they were whole numbers).
 */
const decimalCountSchema = z.union([z.string(), z.number()]).transform(String);
export const nationalityRefSchema = z.object({
    id: z.string(),
    /** In the request's language (Arabic fallback). */
    name: z.string(),
    nameAr: z.string(),
    nameEn: z.string(),
});
export const quantityWordsSchema = z.object({ ar: z.string(), en: z.string() });
// --- PDA: context and customers (section 5.1) -------------------------------
/** GET /pda/context: what the PDA home and purchase screens need. */
export const pdaContextSchema = z.object({
    branch: branchRefSchema,
    businessDate: businessDateSchema,
    /** The products the user may work with (DIESEL_ACCESS / PETROL_ACCESS). */
    products: z.array(productSchema),
    /** `jerrycansInBarrel` (k): the PDA shows the net quantity in jerrycans with it. */
    jerrycansInBarrel: z.string(),
    /**
     * The quantity options of the purchase screen: every category the
     * administrator defined, barrels first, largest fraction first. Loaded on
     * each visit, so a change needs no new application release.
     */
    quantityOptions: z.array(pdaQuantityOptionSchema),
    /** Whether PDA_WHATSAPP_ENABLED is on (section 9): the PDA hides إرسال واتساب when false. */
    whatsappEnabled: z.boolean(),
    /** وضع الجرد per allowed product, for the session branch (fuel.md section 4.4). */
    inventoryModes: inventoryModesSchema,
    /**
     * The products with an open shift (a carried balance on the business date).
     * Without one the PDA blocks every operation of that product (KAN-38).
     */
    openedProducts: z.array(productSchema),
});
/**
 * GET /pda/customers?phone=: a stored phone of an excluded country is still
 * found (customers.md section 9.7); a new customer with one is refused by
 * `pdaNewCustomerSchema`.
 */
export const pdaCustomerLookupQuerySchema = z.object({ phone: storedPhoneSchema });
export const pdaCustomerSchema = z.object({
    id: z.string(),
    phone: z.string(),
    name: z.string().nullable(),
    vehiclePlate: z.string().nullable(),
    /** «نوع المركبة»: optional free text (KAN-51); null when never recorded. */
    vehicleType: z.string().nullable(),
    vehicleNationality: nationalityRefSchema.nullable(),
    driverNationality: nationalityRefSchema.nullable(),
    linkStatus: customerLinkStatusSchema,
    /** The final Customers refusal of a «بانتظار الربط» customer (fuel.md section 8.3). */
    linkError: customerLinkErrorSchema.nullable(),
    /** «رقم مؤكد»: verified by a WhatsApp code and applied in Customers. */
    phoneVerified: z.boolean(),
    /**
     * «إعادة التحقق» is offered: the last registration or phone change failed
     * because its verification receipt expired or was refused.
     */
    reverifyAvailable: z.boolean(),
});
/**
 * GET /pda/customers?phone=: the local customer when one exists (`known`),
 * otherwise the Customers name as a suggestion; Customers being unavailable is
 * not an error (`suggestedName` is null).
 */
export const pdaCustomerLookupSchema = z.object({
    /** The searched phone in E.164. */
    phone: z.string(),
    known: z.boolean(),
    customer: pdaCustomerSchema.nullable(),
    suggestedName: z.string().nullable(),
    /**
     * A new customer's phone needs the WhatsApp code before the review step
     * (fuel-pda.md section 4.2): the switch is on and the phone is not verified
     * in Customers. Always false for a known PDA customer.
     */
    verificationRequired: z.boolean(),
});
// --- PDA: purchase (section 5.2) --------------------------------------------
/** Longest «نوع المركبة». */
export const VEHICLE_TYPE_MAX = 50;
const requiredText = (max) => z.string().trim().min(1).max(max);
/** An optional text: an empty string means "not recorded". */
const optionalText = (max) => z.string().trim().max(max).optional();
/** An existing PDA customer plus only the fields it is still missing. */
export const pdaExistingCustomerSchema = z.object({
    customerId: z.string().min(1),
    name: requiredText(200).optional(),
    vehiclePlate: requiredText(50).optional(),
    vehicleType: optionalText(VEHICLE_TYPE_MAX),
    vehicleNationalityId: z.string().min(1).optional(),
    driverNationalityId: z.string().min(1).optional(),
});
/** A new customer: every field is required. */
export const pdaNewCustomerSchema = z.object({
    phone: pdaPhoneSchema,
    name: requiredText(200),
    vehiclePlate: requiredText(50),
    vehicleType: optionalText(VEHICLE_TYPE_MAX),
    vehicleNationalityId: z.string().min(1),
    driverNationalityId: z.string().min(1),
    /** The verified WhatsApp code of the new customer's phone (fuel-pda.md section 4.2). */
    phoneVerificationId: phoneVerificationIdSchema.optional(),
});
/**
 * The customer part of a purchase: an existing customer when it names
 * `customerId`, otherwise a new one. Picked by that field rather than tried
 * as a union, so a refusal keeps its field and code (`customer.phone` with
 * `PHONE_NOT_MOBILE`) instead of a generic "invalid input" on `customer`.
 */
export const pdaPurchaseCustomerSchema = z
    .unknown()
    .transform((value, ctx) => {
    const existing = typeof value === 'object' && value !== null && 'customerId' in value;
    const result = (existing ? pdaExistingCustomerSchema : pdaNewCustomerSchema).safeParse(value);
    if (result.success)
        return result.data;
    for (const issue of result.error.issues) {
        const params = issue.params;
        ctx.addIssue({
            code: 'custom',
            path: issue.path,
            message: issue.message,
            ...(typeof params?.code === 'string' ? { params: { code: params.code } } : {}),
        });
    }
    return z.NEVER;
});
export const createPdaPurchaseSchema = z.object({
    product: productSchema,
    /**
     * The count of each configured quantity option used. Every `categoryId` must
     * be a current category and appear once; at least one count is above zero.
     */
    lines: z
        .array(z.object({ categoryId: z.string().min(1), qty: containerCountSchema }))
        .max(200)
        .refine((lines) => new Set(lines.map((line) => line.categoryId)).size === lines.length, {
        message: 'each quantity option may appear once',
    }),
    /** عدد الجراكل المخصومة (D), typed without a sign; a decimal string such as `1.5`. */
    deducted: deductedJerrycansSchema.default('0'),
    customer: pdaPurchaseCustomerSchema,
});
/** POST /pda/sales (section 5.2a, KAN-37): the same body as a purchase. */
export const createPdaSaleSchema = createPdaPurchaseSchema;
/** Which PDA entry a receipt or a recent entry belongs to (KAN-37 added the sale). */
export const pdaOperationTypeSchema = z.enum(['PURCHASE', 'SALE']);
/** The WhatsApp message of a PDA purchase, when one was queued. */
export const pdaMessageStatusSchema = z.object({
    id: z.string(),
    status: z.enum(['queued', 'sent', 'delivered', 'read', 'failed', 'invalid_phone']),
});
/**
 * The result screen and the receipt data of section 7: the reference (the PDA
 * draws its QR), the product, the net quantity in words and the operation type.
 * No customer data and no price.
 */
export const pdaReceiptSchema = z.object({
    id: z.string(),
    reference: z.string(),
    product: productSchema,
    operationType: pdaOperationTypeSchema,
    /** B and J: `sum(qty x fraction)` over the barrel and jerrycan categories, decimal strings. */
    barrels: decimalCountSchema,
    jerrycans: decimalCountSchema,
    /** D as a JSON number (up to five decimals, at most 100000: exact in a double). */
    deducted: z.number().min(0),
    /** B x k + J - D, fixed at entry time. */
    netJerrycans: z.string(),
    netQuantity: quantityWordsSchema,
    createdAt: z.iso.datetime({ offset: true }),
    businessDate: businessDateSchema,
    /** Whether sending on WhatsApp is switched on (section 9). */
    whatsappEnabled: z.boolean(),
    /** The WhatsApp message stored for this purchase, if any. */
    message: pdaMessageStatusSchema.nullable(),
    /** PENDING when saved while وضع الجرد is on (fuel.md section 4.4). */
    postingStatus: postingStatusSchema,
});
/** One entry of آخر عملياتي (section 4.4). */
export const pdaRecentPurchaseSchema = z.object({
    reference: z.string(),
    /** Absent from a server older than KAN-37, which listed purchases only. */
    operationType: pdaOperationTypeSchema.default('PURCHASE'),
    createdAt: z.iso.datetime({ offset: true }),
    product: productSchema,
    netQuantity: quantityWordsSchema,
    customerName: z.string().nullable(),
});
/** POST /pda/purchases/:reference/whatsapp (section 9). */
export const pdaWhatsappResultSchema = z.object({
    /** The stored Message id. */
    id: z.string(),
    reference: z.string(),
    status: pdaMessageStatusSchema.shape.status,
});
// --- PDA: sale delivery (section 5.3) ---------------------------------------
/** GET /pda/deliveries/:reference: section 4.3 data; never a price or an amount. */
export const pdaDeliveryLookupSchema = z.object({
    reference: z.string(),
    kind: z.enum(['CASH_SALE', 'PDA_SALE', 'DEBT_SALE']),
    product: productSchema,
    /**
     * PENDING for a PDA sale the office has not completed yet: shown, but not
     * deliverable (`SALE_NOT_COMPLETED`, KAN-37). COMPLETED otherwise. Named
     * without "price": the PDA never sees prices (section 1).
     */
    completionStatus: z.enum(['COMPLETED', 'PENDING']).default('COMPLETED'),
    lines: z.array(z.object({ categoryName: z.string(), qty: z.number().int() })),
    /** «إلا جركل»: deducted jerrycans, a decimal string (0 when none). */
    lessJerrycans: z.string(),
    netQuantity: quantityWordsSchema,
    deliveryStatus: z.enum(['PENDING', 'DELIVERED']),
    deliveredAt: z.iso.datetime({ offset: true }).nullable(),
    deliveredBy: z.string().nullable(),
    /** Debt sales only: the identification data the office recorded (P-10). */
    debtorName: z.string().nullable(),
    vehiclePlate: z.string().nullable(),
});
export const pdaDeliveryResultSchema = z.object({
    /** The delivered transaction's id. */
    id: z.string(),
    reference: z.string(),
    deliveryStatus: z.literal('DELIVERED'),
    deliveredAt: z.iso.datetime({ offset: true }),
    deliveredBy: z.string(),
});
// --- dashboard: purchase pricing (section 6.1) ------------------------------
export const priceStatusSchema = z.enum(['PRICED', 'PENDING']);
export const purchaseSourceSchema = z.enum(['OPERATOR', 'PDA']);
/** The PDA part of a purchase row and its details (null for an operator purchase). */
export const pdaPurchaseInfoSchema = z.object({
    customer: z.object({ id: z.string(), name: z.string().nullable(), phone: z.string() }).nullable(),
    vehiclePlate: z.string().nullable(),
    vehicleType: z.string().nullable(),
    vehicleNationality: z.string().nullable(),
    driverNationality: z.string().nullable(),
    barrels: decimalCountSchema,
    jerrycans: decimalCountSchema,
    deducted: z.number().min(0),
    jerrycansInBarrel: z.string(),
    netJerrycans: z.string(),
    netQuantity: z.string(),
    /** Status of the latest WhatsApp message for this purchase, if any. */
    messageStatus: pdaMessageStatusSchema.shape.status.nullable(),
});
/** GET /purchases and its export add the pricing filter (حالة التسعير). */
export const purchaseListQuerySchema = transactionListQuerySchema.extend({
    priceStatus: priceStatusSchema.optional(),
});
/** POST /purchases/:id/complete-price: both purchase prices, each greater than zero. */
export const completePurchasePriceSchema = z.object({
    barrelPrice: positiveMoneySchema,
    jerrycanPrice: positiveMoneySchema,
});
// --- dashboard: purchase customers (section 6.2) ----------------------------
export const pdaCustomerListQuerySchema = paginationQuerySchema.extend({
    sortBy: z.enum(['name', 'phone', 'vehiclePlate', 'vehicleType', 'createdAt']).optional(),
});
export const pdaCustomerRowSchema = pdaCustomerSchema.extend({
    createdAt: z.iso.datetime({ offset: true }),
    /** «تم الإنشاء بواسطة»: the PDA employee who registered the customer; null when unknown (T2). */
    createdBy: actorSchema.nullable(),
});
/** PATCH /pda-customers/:id: the phone is the identity and is never changed. */
export const updatePdaCustomerSchema = z
    .object({
    /**
     * A new phone (KAN-52): checked against every system through Customers
     * before saving. Read with `storedPhoneSchema`: the service refuses an
     * excluded-country phone only when it changed (customers.md section 9.7).
     */
    phone: storedPhoneSchema,
    name: requiredText(200),
    vehiclePlate: requiredText(50),
    /** An empty string clears the type. */
    vehicleType: z.string().trim().max(VEHICLE_TYPE_MAX),
    vehicleNationalityId: z.string().min(1),
    driverNationalityId: z.string().min(1),
    /** The verified WhatsApp code of a phone change (fuel-pda.md section 6.2). */
    phoneVerificationId: phoneVerificationIdSchema,
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, 'at least one field');
// --- dashboard: nationalities (section 6.3) ---------------------------------
export const nationalityRowSchema = z.object({
    id: z.string(),
    nameAr: z.string(),
    nameEn: z.string(),
    createdAt: z.iso.datetime({ offset: true }),
});
/** POST /nationalities and PATCH /nationalities/:id: both names are required and unique. */
export const saveNationalitySchema = z.object({
    nameAr: requiredText(100),
    nameEn: requiredText(100),
});
//# sourceMappingURL=pda.contract.js.map